import React, { useCallback, useEffect, useRef, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { LayoutGrid, Check, RotateCcw, Eye, ChevronDown, ChevronUp, RefreshCw, AlertTriangle } from "lucide-react";
import { DashboardTile } from "@/components/dashboard/DashboardTile";
import { TILE_META } from "@/components/dashboard/tileContent";
import DashboardReports from "@/pages/DashboardReports";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

const Kpi = ({ label, value, tone, testId }) => (
  <div className="bg-white border border-[#E9ECEF] rounded-lg px-4 py-3 shadow-[0_1px_2px_rgba(11,19,43,0.04)]" data-testid={testId}>
    <div className={`font-mono text-2xl sm:text-3xl font-black leading-none ${tone === "bad" ? "text-red-600" : "text-[#0B132B]"}`}>{fmt(value)}</div>
    <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] mt-1.5">{label}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [layout, setLayout] = useState(null);
  const [customized, setCustomized] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [customizing, setCustomizing] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const saveTimer = useRef(null);

  const loadSummary = useCallback(() => api.get("/dashboard/summary").then(({ data }) => { setSummary(data); setError(null); }).catch(() => setError("Couldn't load dashboard metrics")), []);
  useEffect(() => {
    api.get("/dashboard/layout").then(({ data }) => { setLayout(data.tiles); setCustomized(data.customized); }).catch(() => setError("Couldn't load your dashboard layout"));
    loadSummary();
  }, [loadSummary]);

  const persist = (tiles) => {
    setLayout(tiles); setCustomized(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => api.put("/dashboard/layout", { tiles }).catch(() => toast.error("Could not save layout")), 400);
  };
  const visible = (layout || []).filter((t) => !t.hidden);
  const hidden = (layout || []).filter((t) => t.hidden);
  const move = (key, dir) => {
    const idx = visible.findIndex((t) => t.key === key); const j = idx + dir;
    if (j < 0 || j >= visible.length) return;
    const reordered = arrayMove(visible, idx, j);
    persist([...reordered, ...hidden]);
  };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const a = visible.findIndex((t) => t.key === active.id), b = visible.findIndex((t) => t.key === over.id);
    persist([...arrayMove(visible, a, b), ...hidden]);
  };
  const resize = (key, size) => persist(layout.map((t) => (t.key === key ? { ...t, size } : t)));
  const setHidden = (key, val) => persist(layout.map((t) => (t.key === key ? { ...t, hidden: val } : t)));
  const reset = async () => { try { const { data } = await api.delete("/dashboard/layout"); setLayout(data.tiles); setCustomized(false); toast.success("Dashboard reset to default"); } catch { toast.error("Reset failed"); } };
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const k = summary?.kpis || {};
  const first = (user?.name || "").split(" ")[0];

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="dashboard-page">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">{new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}</div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[#0B132B] tracking-tight leading-none mt-1" data-testid="dashboard-greeting">{first ? `Welcome back, ${first}` : "Dashboard"}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="h-9 rounded-sm text-[#6C757D]" onClick={loadSummary} aria-label="Refresh" data-testid="dashboard-refresh"><RefreshCw size={14} /></Button>
            {customizing && customized && <Button variant="outline" size="sm" className="h-9 rounded-sm" onClick={reset} data-testid="dashboard-reset"><RotateCcw size={13} className="mr-1" /> Reset to default</Button>}
            <Button size="sm" className={`h-9 rounded-sm ${customizing ? "bg-emerald-700 hover:bg-emerald-800" : "bg-[#0B132B] hover:bg-[#1C2541]"} text-white`} onClick={() => setCustomizing((c) => !c)} data-testid="dashboard-customize">
              {customizing ? <><Check size={14} className="mr-1" /> Done</> : <><LayoutGrid size={14} className="mr-1" /> Customize</>}
            </Button>
          </div>
        </div>

        {error && <div className="mb-4 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="dashboard-error"><AlertTriangle size={14} /> {error} <button className="underline ml-auto" onClick={loadSummary}>Retry</button></div>}

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" aria-label="Performance summary" data-testid="dashboard-kpis">
          <Kpi label="Dealers in pipeline" value={k.pipeline} testId="kpi-pipeline" />
          <Kpi label="Signed dealers MTD" value={k.signed_mtd} testId="kpi-signed-mtd" />
          <Kpi label="Installed dealers MTD" value={k.installed_mtd} testId="kpi-installed-mtd" />
          <Kpi label="Overdue onboarding SLAs" value={k.overdue_slas} tone={k.overdue_slas ? "bad" : undefined} testId="kpi-overdue-slas" />
        </section>

        {customizing && <p className="text-xs text-[#6C757D] mb-3" data-testid="customize-hint">Drag tiles by the handle, or use the arrow buttons to reorder. Pick a size, or hide tiles you don't need. Changes save automatically to your account.</p>}

        {!layout ? <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-6 gap-3">{[...Array(6)].map((_, i) => <div key={i} className="col-span-1 md:col-span-2 h-36 bg-white border border-[#E9ECEF] rounded-lg animate-pulse" />)}</div> : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={visible.map((t) => t.key)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-6 gap-3 items-start" data-testid="dashboard-tiles">
                {visible.map((t, i) => <DashboardTile key={t.key} tile={t} index={i} total={visible.length} data={summary?.tiles?.[t.key]} customizing={customizing} onMove={move} onResize={resize} onHide={(key) => setHidden(key, true)} />)}
              </div>
            </SortableContext>
          </DndContext>)}
        {layout && visible.length === 0 && <div className="text-sm text-[#6C757D] italic text-center py-10" data-testid="dashboard-empty">All tiles are hidden. Restore some below.</div>}

        {customizing && hidden.length > 0 && (
          <div className="mt-4 bg-white border border-dashed border-[#E9ECEF] rounded-lg p-3" data-testid="hidden-tiles">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] mb-2">Hidden tiles</div>
            <div className="flex flex-wrap gap-2">{hidden.map((t) => <button key={t.key} type="button" onClick={() => setHidden(t.key, false)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0B132B] border border-[#E9ECEF] rounded-sm px-2.5 py-1.5 hover:border-[#0B132B]/40" data-testid={`restore-${t.key}`}><Eye size={12} /> {TILE_META[t.key].label}</button>)}</div>
          </div>)}

        <section className="mt-8 bg-white border border-[#E9ECEF] rounded-lg" data-testid="reports-section">
          <button type="button" onClick={() => setReportsOpen((o) => !o)} aria-expanded={reportsOpen} className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid="reports-toggle">
            <div><div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">Reports</div><div className="text-sm font-bold text-[#0B132B] mt-0.5">Detailed reporting — proposals, proformas, dealer agreements, rankings & contracts</div></div>
            {reportsOpen ? <ChevronUp size={16} className="text-[#6C757D]" /> : <ChevronDown size={16} className="text-[#6C757D]" />}
          </button>
          {reportsOpen && <div className="px-4 sm:px-6 pb-6 border-t border-[#E9ECEF] pt-5">{<DashboardReports />}</div>}
        </section>
      </main>
    </div>
  );
}
